import React, { useState } from 'react';
import { Card, Switch, Space, Typography, Button } from 'antd';
import { RobotOutlined } from '@ant-design/icons';
import ApexCharts from 'apexcharts';
import ReactApexChart from 'react-apexcharts';

const { Text } = Typography;

const StockChart = ({ stockData, stockCode, stockName, stockId, onAIAnalysis }) => {
  const [showMA5, setShowMA5] = useState(true);
  const [showMA20, setShowMA20] = useState(true);
  const [showMA60, setShowMA60] = useState(false);
  const [showMA120, setShowMA120] = useState(false);

  if (!stockData || stockData.length === 0) {
    return (
      <Card title={`${stockName || stockCode} - Stock Chart`}>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <div style={{ fontSize: '16px', color: '#999', marginBottom: '8px' }}>
            No data available for {stockName || stockCode}
          </div>
          <div style={{ fontSize: '14px', color: '#ccc' }}>
            Please check the stock code or try a different date range
          </div>
        </div>
      </Card>
    );
  }

  // 이동평균 계산 함수
  const calculateMovingAverage = (data, period) => {
    const result = [];
    for (let i = 0; i < data.length; i++) {
      if (i < period - 1) {
        result.push({ x: new Date(data[i].date).getTime(), y: null });
      } else {
        const sum = data.slice(i - period + 1, i + 1).reduce((acc, item) => acc + item.close, 0);
        const average = sum / period;
        result.push({ x: new Date(data[i].date).getTime(), y: average });
      }
    }
    return result;
  };

  // ApexCharts용 데이터 변환
  const chartData = stockData.map(item => ({
    x: new Date(item.date).getTime(),
    y: [item.open, item.high, item.low, item.close]
  }));

  const volumeData = stockData.map(item => ({
    x: new Date(item.date).getTime(),
    y: item.volume
  }));

  // 이동평균 데이터 계산
  const ma5Data = calculateMovingAverage(stockData, 5);
  const ma20Data = calculateMovingAverage(stockData, 20);
  const ma60Data = calculateMovingAverage(stockData, 60);
  const ma120Data = calculateMovingAverage(stockData, 120);

  // 차트 시리즈 구성
  const chartSeries = [
    {
      name: 'Price',
      type: 'candlestick',
      data: chartData
    }
  ];

  if (showMA5) {
    chartSeries.push({
      name: 'MA5',
      type: 'line',
      data: ma5Data
    });
  }

  if (showMA20) {
    chartSeries.push({
      name: 'MA20',
      type: 'line',
      data: ma20Data
    });
  }

  if (showMA60) {
    chartSeries.push({
      name: 'MA60',
      type: 'line',
      data: ma60Data
    });
  }

  if (showMA120) {
    chartSeries.push({
      name: 'MA120',
      type: 'line',
      data: ma120Data
    });
  }

  const candlestickOptions = {
    chart: {
      type: 'candlestick',
      height: 400,
      id: 'candles',
      toolbar: {
        autoSelected: 'pan',
        show: true
      },
      zoom: {
        enabled: true
      },
    },
    title: {
      text: `${stockName ? `${stockName} (${stockCode})` : stockCode} Stock Price with Moving Averages`,
      align: 'left'
    },
    xaxis: {
      type: 'datetime'
    },
    yaxis: {
      tooltip: {
        enabled: true
      }
    },
    plotOptions: {
      candlestick: {
        colors: {
          upward: '#26a69a',
          downward: '#ef5350'
        }
      }
    },
    stroke: {
      width: [1, 2, 2, 2, 2], // 첫 번째는 candlestick(무시됨), 나머지는 이동평균선 두께
      curve: 'smooth'
    },
    colors: ['#000000', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4'], // Price, MA5, MA20, MA60, MA120
    legend: {
      show: true,
      position: 'top',
      horizontalAlign: 'left'
    },
    tooltip: {
      shared: true,
      custom: function({ seriesIndex, dataPointIndex, w }) {
        const data = w.globals.initialSeries;
        let tooltipContent = '<div class="apexcharts-tooltip-custom" style="padding: 8px;">';
        
        if (data[0] && data[0].data[dataPointIndex]) {
          const priceData = data[0].data[dataPointIndex];
          const date = new Date(priceData.x).toLocaleDateString();
          tooltipContent += `<div><strong>${date}</strong></div>`;
          tooltipContent += `<div>Open: ₩${priceData.y[0].toLocaleString()}</div>`;
          tooltipContent += `<div>High: ₩${priceData.y[1].toLocaleString()}</div>`;
          tooltipContent += `<div>Low: ₩${priceData.y[2].toLocaleString()}</div>`;
          tooltipContent += `<div>Close: ₩${priceData.y[3].toLocaleString()}</div>`;
        }
        
        // 이동평균 정보 추가
        for (let i = 1; i < data.length; i++) {
          if (data[i] && data[i].data[dataPointIndex] && data[i].data[dataPointIndex].y !== null) {
            tooltipContent += `<div style="color: ${w.globals.colors[i]};">${data[i].name}: ₩${data[i].data[dataPointIndex].y.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}</div>`;
          }
        }
        
        tooltipContent += '</div>';
        return tooltipContent;
      }
    }
  };

  const volumeOptions = {
    chart: {
      height: 160,
      type: 'bar',
      id: 'volume-chart',
      toolbar: {
        show: false
      }
    },
    colors: ['#008FFB'],
    title: {
      text: 'Volume',
      align: 'left'
    },
    xaxis: {
      type: 'datetime',
      axisBorder: {
        offsetX: 13
      }
    },
    yaxis: {
      labels: {
        show: false
      }
    },
    dataLabels: {
      enabled: false
    }
  };

  return (
    <div>
      <Card 
        title={`${stockName ? `${stockName} (${stockCode})` : stockCode} - Stock Analysis`} 
        style={{ marginBottom: 16 }}
        extra={
          <Space wrap>
            {onAIAnalysis && (
              <Button
                type="primary"
                icon={<RobotOutlined />}
                onClick={onAIAnalysis}
                size="small"
              >
                AI Analysis
              </Button>
            )}
            <Space>
              <Text>MA5:</Text>
              <Switch 
                checked={showMA5} 
                onChange={setShowMA5} 
                size="small"
              />
            </Space>
            <Space>
              <Text>MA20:</Text>
              <Switch 
                checked={showMA20} 
                onChange={setShowMA20} 
                size="small"
              />
            </Space>
            <Space>
              <Text>MA60:</Text>
              <Switch 
                checked={showMA60} 
                onChange={setShowMA60} 
                size="small"
              />
            </Space>
            <Space>
              <Text>MA120:</Text>
              <Switch 
                checked={showMA120} 
                onChange={setShowMA120} 
                size="small"
              />
            </Space>
          </Space>
        }
      >
        <ReactApexChart
          options={candlestickOptions}
          series={chartSeries}
          type="line"
          height={400}
        />
      </Card>
      
      <Card title={`${stockName ? `${stockName} (${stockCode})` : stockCode} - Volume`}>
        <ReactApexChart
          options={volumeOptions}
          series={[{ name: 'Volume', data: volumeData }]}
          type="bar"
          height={160}
        />
      </Card>
    </div>
  );
};

export default StockChart;
